import { Briefcase, GraduationCap, Coins, BookOpen, Heart, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CongoMapAnimation } from './CongoMapAnimation';

const categories = [
  {
    id: 'emploi',
    title: 'Emplois',
    description: 'CDI, CDD et freelance',
    icon: Briefcase,
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  {
    id: 'stage',
    title: 'Stages',
    description: 'Rémunérés et non rémunérés',
    icon: GraduationCap,
    gradient: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
  },
  {
    id: 'bourse',
    title: 'Bourses',
    description: 'Locales et internationales',
    icon: Coins,
    gradient: 'from-emerald-500 to-teal-500',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
  },
  {
    id: 'formation',
    title: 'Formations',
    description: 'Professionnelles',
    icon: BookOpen,
    gradient: 'from-honor-orange to-orange-500',
    bgLight: 'bg-orange-50',
    textColor: 'text-honor-orange',
  },
  {
    id: 'volontariat',
    title: 'Volontariat',
    description: 'Missions de bénévolat',
    icon: Heart,
    gradient: 'from-rose-500 to-pink-500',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-600',
  }
];

export function OpportunityCategories() {
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchCounts = async () => {
      const { data } = await supabase
        .from('opportunities')
        .select('type')
        .eq('status', 'approved');
      
      const counts = data?.reduce((acc, opp) => {
        acc[opp.type] = (acc[opp.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      setCategoryCounts(counts);
    };
    fetchCounts();
  }, []);
  
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-honor-orange/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Categories */}
          <div>
            {/* Section Header */}
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-12 h-[2px] bg-honor-orange" />
                <span className="text-honor-orange font-medium tracking-wide uppercase text-sm">
                  Catégories
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
                Explorez par<br />catégorie
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                Trouvez l'opportunité qui correspond à vos ambitions
              </p>
            </div>
            
            {/* Stacked Category Cards */}
            <div className="space-y-3">
              {categories.map((category, index) => {
                const Icon = category.icon;
                const count = categoryCounts[category.id] || 0;
                
                return (
                  <Link
                    key={category.id}
                    to={`/opportunities?type=${category.id}`}
                    className="group flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:border-honor-orange/50 transition-all duration-300 hover:bg-white/10 hover:translate-x-2"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-white group-hover:text-honor-orange transition-colors">
                        {category.title}
                      </h3>
                      <p className="text-sm text-slate-400 truncate">
                        {category.description}
                      </p>
                    </div>
                    
                    {/* Count & Arrow */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-sm font-bold ${category.textColor} bg-white/10 px-3 py-1 rounded-full`}>
                        {count}
                      </span>
                      <div className="w-10 h-10 rounded-full bg-white/5 group-hover:bg-honor-orange flex items-center justify-center transition-all duration-300">
                        <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* Right side - Animated Congo Map */}
          <div className="relative h-[500px] lg:h-[600px] hidden md:block">
            <CongoMapAnimation />
          </div>
        </div>
      </div>
    </section>
  );
}
